import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const TenantContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children, tenantSlug }) => {
  const [tenant, setTenant] = useState(null);
  const [tenantUser, setTenantUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const TENANT_TOKEN_KEY = `fluxoco_tenant_${tenantSlug}_token`;
  const TENANT_USER_KEY = `fluxoco_tenant_${tenantSlug}_user`;

  useEffect(() => {
    if (tenantSlug) {
      loadTenantInfo(tenantSlug);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  useEffect(() => {
    if (tenant) {
      document.title = `${tenant.name} | Fluxo Co Portal`;
      
      // Update Favicon based on logo (Only for guest tenants, not for Master Console)
      const isMasterConsole = !tenantSlug || tenantSlug === 'fluxo' || tenantSlug === 'master';
      
      if (tenant.logo_url && !isMasterConsole) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        link.href = tenant.logo_url;
        
        if (!document.querySelector("link[rel~='icon']")) {
          document.getElementsByTagName('head')[0].appendChild(link);
        }
      }
    }
  }, [tenant]);

  const loadTenantInfo = async (slug) => {
    if (!slug) return;
    
    // Try to load from cache first for instant UI response
    const cacheKey = `fluxoco_branding_${slug}`;
    const cachedBranding = localStorage.getItem(cacheKey);
    if (cachedBranding) {
      try {
        const parsed = JSON.parse(cachedBranding);
        setTenant(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.warn("Error parsing branding cache", e);
      }
    }

    setLoading(true);
    try {
      // Load tenant info by slug
      const response = await axios.get(`${API_URL}/api/tenant-portal/${slug}/info`);
      console.log("Fluxo Co: Tenant info loaded:", response.data);
      setTenant(response.data);
      
      // Cache branding info for instant load next time
      localStorage.setItem(cacheKey, JSON.stringify({
        primary_color: response.data.primary_color,
        logo_url: response.data.logo_url,
        name: response.data.name
      }));

      // Check if user is already logged in
      const token = localStorage.getItem(TENANT_TOKEN_KEY);
      const savedUser = localStorage.getItem(TENANT_USER_KEY);
      
      if (token && savedUser) {
        setTenantUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        axios.defaults.headers.common['X-Tenant-ID'] = response.data.id;
      }
    } catch (error) {
      console.error('Error loading tenant:', error);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  };

  const loginTenant = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/tenant-portal/${tenantSlug}/login`, {
        email,
        password
      });

      const { access_token, user, tenant: tenantData } = response.data;
      
      localStorage.setItem(TENANT_TOKEN_KEY, access_token);
      localStorage.setItem(TENANT_USER_KEY, JSON.stringify(user));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      axios.defaults.headers.common['X-Tenant-ID'] = tenantData.id;
      
      setTenantUser(user);
      setTenant(tenantData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const logoutTenant = () => {
    localStorage.removeItem(TENANT_TOKEN_KEY);
    localStorage.removeItem(TENANT_USER_KEY);
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Tenant-ID'];
    setTenantUser(null);
    setIsAuthenticated(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/tenant-portal/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al cambiar la contraseña'
      };
    }
  };

  return (
    <TenantContext.Provider value={{
      tenant,
      tenantUser,
      tenantSlug,
      loading,
      isAuthenticated,
      loginTenant,
      logoutTenant,
      changePassword,
      refreshTenantData: loadTenantInfo
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
